import React, { useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, Skeleton, Container, Stack, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import theme from './theme';
import { UserRole } from './types/enums';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NasabahProvider } from './contexts/NasabahContext';
import { PermissionsProvider, usePermissions } from './contexts/PermissionsContext';
import { TabNavProvider } from './contexts/TabNavContext';
import { PinGateProvider } from './contexts/PinGateContext';
import { useBranchId } from './hooks/useBranchId';
import { usePendingApprovalCount } from './hooks/usePendingApprovalCount';
import DashboardHeader from './components/dashboard/DashboardHeader';
import NavigationDrawer from './components/dashboard/NavigationDrawer';
import {
  HouseOutlined,
  HistoryOutlined,
  Send,
  PersonOutlined,
  Recycling,
  SyncAltOutlined,
  CheckCircleOutline,
  GroupsOutlined,
  BadgeOutlined,
  PriceCheck,
  Assessment,
  BusinessOutlined,
  SettingsOutlined,
  ShieldOutlined,
  AssignmentOutlined,
  HowToRegOutlined,
  AccountBalanceOutlined,
  AccountBalanceWalletOutlined,
  Campaign,
} from '@mui/icons-material';

// Lazy load components for better performance
const LandingPage = lazy(() => import('./components/landing/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));
const BranchSelectionPage = lazy(() => import('./pages/BranchSelectionPage'));
const AktivasiPage = lazy(() => import('./pages/AktivasiPage'));
const NasabahHome = lazy(() => import('./features/nasabah/components/NasabahHome'));
const PengelolaDashboard = lazy(() => import('./features/pengelola/components/PengelolaDashboard'));
const AdminBankSampahDashboard = lazy(() => import('./features/admin/components/AdminBankSampahDashboard'));
const MobileTabBar = lazy(() => import('./shared/components/MobileTabBar'));

// Banking feature pages
const UserProfilePage = lazy(() => import('./features/nasabah/pages/UserProfilePage'));
const TransactionHistoryPage = lazy(() => import('./features/nasabah/pages/TransactionHistoryPage'));
const TransferPage = lazy(() => import('./features/nasabah/pages/TransferPage'));
const StatistikSampahPage = lazy(() => import('./features/nasabah/pages/StatistikSampahPage'));
const NasabahWastePriceCheckPage = lazy(() => import('./pages/WastePriceCheckPage'));

// Pengelola feature pages
const NasabahPage = lazy(() => import('./pages/pengelola/NasabahPage'));
const AktivasiNasabahPage = lazy(() => import('./pages/pengelola/AktivasiNasabahPage'));
const InventoryPage = lazy(() => import('./pages/pengelola/InventoryPage'));
const StatistikPage = lazy(() => import('./pages/pengelola/StatistikPage'));
const PengaturanHargaPage = lazy(() => import('./pages/pengelola/PengaturanHargaPage'));
const MasterJenisSampahPage = lazy(() => import('./pages/pengelola/MasterJenisSampahPage'));
const RiwayatPembayaranPage = lazy(() => import('./pages/pengelola/RiwayatPembayaranPage'));
const NotifikasiPage = lazy(() => import('./pages/pengelola/NotifikasiPage'));
const BackupExportPage = lazy(() => import('./pages/pengelola/BackupExportPage'));
const ManajemenPenggunaPage = lazy(() => import('./pages/pengelola/ManajemenPenggunaPage'));
const ProfilBankSampahPage = lazy(() => import('./pages/pengelola/ProfilBankSampahPage'));
const ApprovalCenterPage = lazy(() => import('./pages/pengelola/ApprovalCenterPage'));
const SetoranPage = lazy(() => import('./pages/pengelola/SetoranPage'));
const KasPenjualanPage = lazy(() => import('./pages/pengelola/KasPenjualanPage'));

// Super Admin feature pages
const SuperAdminDashboardPage    = lazy(() => import('./features/super-admin/pages/SuperAdminDashboardPage'));
const BranchManagementPage       = lazy(() => import('./features/super-admin/pages/BranchManagementPage'));
const BankSampahOnboardingPage   = lazy(() => import('./features/super-admin/pages/BankSampahOnboardingPage'));
const AuditLogsPage              = lazy(() => import('./pages/super-admin/AuditLogsPage'));
const SystemSettingsPage         = lazy(() => import('./pages/super-admin/SystemSettingsPage'));
const RbacPage                   = lazy(() => import('./pages/super-admin/RbacPage'));

// Onboarding pages
const BankSampahRegisterPage  = lazy(() => import('./pages/BankSampahRegisterPage'));
const OnboardingPage          = lazy(() => import('./pages/pengelola/OnboardingPage'));

const AppContainer = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#FFFFFF',
});

// Loading component for better UX
const PageSkeleton: React.FC = () => (
  <Container maxWidth="lg">
    <Stack spacing={4} sx={{ py: 4 }}>
      <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2, flex: 1 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2, flex: 1 }} />
      </Stack>
      <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
    </Stack>
  </Container>
);

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <PageSkeleton />;
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Logout route — panggil logout lalu arahkan ke landing page.
const LogoutRedirect: React.FC = () => {
  const { logout, isAuthenticated, loading } = useAuth();
  const [done, setDone] = React.useState(false);
  React.useEffect(() => {
    if (!loading && !done) {
      setDone(true);
      if (isAuthenticated) logout();
    }
  }, [loading, isAuthenticated, logout, done]);
  return <Navigate to="/" replace />;
};

// Public-only route: jika sudah login, redirect ke /dashboard.
// Dipakai untuk /login dan /register agar session aktif tidak terinterupsi.
const PublicOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <PageSkeleton />;
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <>{children}</>;
};

// Role-protected route: butuh autentikasi + salah satu role yang diizinkan.
// Penegakan sebenarnya tetap di RLS; ini hanya guard UX agar role yang tidak
// berwenang (mis. NASABAH) tidak membuka halaman pengelola via URL langsung.
const RoleProtectedRoute: React.FC<{ allow: readonly UserRole[]; children: React.ReactNode }> = ({ allow, children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) return <PageSkeleton />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user || !allow.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// SUPER_ADMIN adalah role platform saja (tidak boleh akses halaman operasional tenant).
// Lihat docs/fix_database.md dan src/lib/permissions.ts untuk matriks lengkap.
const TENANT_OPERATIONAL_ROLES: readonly UserRole[] = [
  UserRole.PENGELOLA,
  UserRole.ADMIN_BANK_SAMPAH,
];
const TENANT_ADMIN_ROLES: readonly UserRole[] = [
  UserRole.ADMIN_BANK_SAMPAH,
];

// Definisi tab per role. Tiap tab terikat ke `resource` RBAC sehingga
// visibilitas menu mengikuti matriks hak akses (dapat diatur Super Admin).
interface TabDef {
  key: string;
  resource: string;
  label: string;
  icon: React.ReactNode;
  element: React.ReactNode;
  /** Kelompok menu di sidebar (menu serupa diklasifikasikan bersama). */
  group: string;
}

const TABS_BY_ROLE: Record<UserRole, TabDef[]> = {
  [UserRole.NASABAH]: [
    { key: 'beranda', resource: 'dashboard', label: 'Beranda', icon: <HouseOutlined />, element: <NasabahHome />, group: 'Utama' },
    { key: 'riwayat', resource: 'transaksi', label: 'Riwayat', icon: <HistoryOutlined />, element: <TransactionHistoryPage />, group: 'Aktivitas' },
    { key: 'statistik', resource: 'statistik', label: 'Statistik', icon: <Assessment />, element: <StatistikSampahPage />, group: 'Aktivitas' },
    { key: 'profil', resource: 'profil', label: 'Profil', icon: <PersonOutlined />, element: <UserProfilePage />, group: 'Akun' },
  ],
  [UserRole.PENGELOLA]: [
    { key: 'dashboard', resource: 'dashboard', label: 'Dashboard', icon: <HouseOutlined />, element: <PengelolaDashboard />, group: 'Utama' },
    { key: 'setoran', resource: 'transaksi', label: 'Setoran', icon: <Recycling />, element: <SetoranPage />, group: 'Transaksi & Kas' },
    { key: 'transaksi', resource: 'transaksi', label: 'Transaksi', icon: <SyncAltOutlined />, element: <RiwayatPembayaranPage />, group: 'Transaksi & Kas' },
    { key: 'approval', resource: 'approval', label: 'Approval', icon: <CheckCircleOutline />, element: <ApprovalCenterPage />, group: 'Transaksi & Kas' },
    { key: 'kas', resource: 'kas', label: 'Kas', icon: <AccountBalanceWalletOutlined />, element: <KasPenjualanPage />, group: 'Transaksi & Kas' },
    { key: 'nasabah', resource: 'nasabah', label: 'Nasabah', icon: <GroupsOutlined />, element: <NasabahPage />, group: 'Keanggotaan' },
    { key: 'aktivasi', resource: 'nasabah', label: 'Aktivasi', icon: <HowToRegOutlined />, element: <AktivasiNasabahPage />, group: 'Keanggotaan' },
    { key: 'notifikasi', resource: 'notifikasi', label: 'Notifikasi', icon: <Campaign />, element: <NotifikasiPage />, group: 'Komunikasi' },
    { key: 'laporan', resource: 'laporan', label: 'Laporan', icon: <Assessment />, element: <StatistikPage />, group: 'Laporan & Audit' },
    { key: 'audit', resource: 'audit', label: 'Audit', icon: <HistoryOutlined />, element: <AuditLogsPage />, group: 'Laporan & Audit' },
  ],
  [UserRole.ADMIN_BANK_SAMPAH]: [
    { key: 'dashboard', resource: 'dashboard', label: 'Dashboard', icon: <HouseOutlined />, element: <AdminBankSampahDashboard />, group: 'Utama' },
    { key: 'nasabah', resource: 'nasabah', label: 'Nasabah', icon: <GroupsOutlined />, element: <NasabahPage />, group: 'Keanggotaan' },
    { key: 'aktivasi', resource: 'nasabah', label: 'Aktivasi', icon: <HowToRegOutlined />, element: <AktivasiNasabahPage />, group: 'Keanggotaan' },
    { key: 'pengguna', resource: 'pengguna', label: 'Pengelola', icon: <BadgeOutlined />, element: <ManajemenPenggunaPage />, group: 'Keanggotaan' },
    { key: 'setoran', resource: 'transaksi', label: 'Setoran', icon: <Recycling />, element: <SetoranPage />, group: 'Operasional' },
    { key: 'approval', resource: 'approval', label: 'Approval', icon: <CheckCircleOutline />, element: <ApprovalCenterPage />, group: 'Operasional' },
    { key: 'kas', resource: 'kas', label: 'Kas', icon: <AccountBalanceWalletOutlined />, element: <KasPenjualanPage />, group: 'Operasional' },
    { key: 'harga', resource: 'harga', label: 'Jenis Sampah', icon: <PriceCheck />, element: <MasterJenisSampahPage />, group: 'Operasional' },
    { key: 'laporan', resource: 'laporan', label: 'Laporan', icon: <Assessment />, element: <StatistikPage />, group: 'Laporan & Audit' },
    { key: 'audit', resource: 'audit', label: 'Audit', icon: <HistoryOutlined />, element: <AuditLogsPage />, group: 'Laporan & Audit' },
    { key: 'profilbs', resource: 'bank_sampah', label: 'Profil BS', icon: <BusinessOutlined />, element: <ProfilBankSampahPage />, group: 'Organisasi' },
    { key: 'onboarding', resource: 'bank_sampah', label: 'Onboarding', icon: <AssignmentOutlined />, element: <OnboardingPage />, group: 'Organisasi' },
  ],
  [UserRole.SUPER_ADMIN]: [
    { key: 'platform', resource: 'dashboard', label: 'Platform', icon: <Assessment />, element: <SuperAdminDashboardPage />, group: 'Utama' },
    { key: 'onboarding', resource: 'bank_sampah', label: 'Onboarding BS', icon: <HowToRegOutlined />, element: <BankSampahOnboardingPage />, group: 'Organisasi' },
    { key: 'banksampah', resource: 'bank_sampah', label: 'Cabang Fisik', icon: <AccountBalanceOutlined />, element: <BranchManagementPage />, group: 'Organisasi' },
    { key: 'nasabah', resource: 'nasabah', label: 'Nasabah', icon: <GroupsOutlined />, element: <NasabahPage />, group: 'Keanggotaan' },
    { key: 'aktivasi', resource: 'nasabah', label: 'Aktivasi', icon: <HowToRegOutlined />, element: <AktivasiNasabahPage />, group: 'Keanggotaan' },
    { key: 'pengguna', resource: 'pengguna', label: 'Pengguna', icon: <BadgeOutlined />, element: <ManajemenPenggunaPage />, group: 'Keanggotaan' },
    { key: 'notifikasi', resource: 'notifikasi', label: 'Pengumuman', icon: <Campaign />, element: <NotifikasiPage />, group: 'Komunikasi' },
    { key: 'audit', resource: 'audit', label: 'Audit', icon: <HistoryOutlined />, element: <AuditLogsPage />, group: 'Sistem' },
    { key: 'pengaturan', resource: 'pengaturan', label: 'Pengaturan', icon: <SettingsOutlined />, element: <SystemSettingsPage />, group: 'Sistem' },
    { key: 'rbac', resource: 'rbac', label: 'Hak Akses', icon: <ShieldOutlined />, element: <RbacPage />, group: 'Sistem' },
  ],
};

// Menu utama di bottom bar — maksimal 4 (mobile) / 5 (desktop & tablet).
// Menu selengkapnya diakses lewat sidebar (tombol burger di header).
const PRIMARY_NAV_BY_ROLE: Record<UserRole, { mobile: string[]; desktop: string[] }> = {
  [UserRole.NASABAH]: {
    mobile: ['beranda', 'riwayat', 'statistik', 'profil'],
    desktop: ['beranda', 'riwayat', 'statistik', 'profil'],
  },
  [UserRole.PENGELOLA]: {
    mobile: ['dashboard', 'setoran', 'approval', 'nasabah'],
    desktop: ['dashboard', 'setoran', 'transaksi', 'approval', 'nasabah'],
  },
  [UserRole.ADMIN_BANK_SAMPAH]: {
    mobile: ['dashboard', 'nasabah', 'approval', 'laporan'],
    desktop: ['dashboard', 'nasabah', 'pengguna', 'approval', 'laporan'],
  },
  [UserRole.SUPER_ADMIN]: {
    mobile: ['platform', 'onboarding', 'pengguna', 'pengaturan'],
    desktop: ['platform', 'onboarding', 'banksampah', 'pengguna', 'pengaturan'],
  },
};

// Dashboard wrapper — daftar tab difilter oleh permission `resource.view`.
const DashboardWrapper: React.FC<{ currentTab: number; onTabChange: (event: React.SyntheticEvent, newValue: number) => void }> = ({ currentTab, onTabChange }) => {
  const { user, effectiveRole } = useAuth();
  const { can } = usePermissions();
  // effectiveRole = NASABAH ketika staf sedang di Mode Nasabah — seluruh
  // tab, bottom bar, dan sidebar mengikuti tampilan nasabah.
  const userRole = effectiveRole || UserRole.NASABAH;

  const allTabs = TABS_BY_ROLE[userRole] ?? TABS_BY_ROLE[UserRole.NASABAH];
  const visibleTabs = allTabs.filter((t) => can(t.resource, 'view'));
  const safeIndex = Math.min(Math.max(currentTab, 0), Math.max(visibleTabs.length - 1, 0));
  const active = visibleTabs[safeIndex] ?? visibleTabs[0];

  // Lencana jumlah approval menunggu — hanya untuk staf cabang (realtime).
  const isTenantStaff =
    userRole === UserRole.PENGELOLA || userRole === UserRole.ADMIN_BANK_SAMPAH;
  const { branchId } = useBranchId(isTenantStaff ? user : null);
  const pendingApprovals = usePendingApprovalCount(isTenantStaff ? branchId : null);

  // Memungkinkan aksi cepat (mis. di Beranda) berpindah tab berdasarkan key.
  const goToTab = (key: string) => {
    const idx = visibleTabs.findIndex((t) => t.key === key);
    if (idx >= 0) onTabChange({} as React.SyntheticEvent, idx);
  };

  // Sidebar navigasi (burger) — memuat seluruh menu, dikelompokkan per kategori.
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Bottom bar hanya memuat menu utama: maks 4 di mobile, 5 di tablet/desktop.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const primaryDef = PRIMARY_NAV_BY_ROLE[userRole] ?? PRIMARY_NAV_BY_ROLE[UserRole.NASABAH];
  const primaryKeys = isMobile ? primaryDef.mobile : primaryDef.desktop;
  const primaryTabs = visibleTabs
    .filter((t) => primaryKeys.includes(t.key))
    .slice(0, isMobile ? 4 : 5);

  const badgeFor = (key: string) => (key === 'approval' ? pendingApprovals : undefined);

  return (
    <AppContainer>
      <DashboardHeader onMenuClick={() => setDrawerOpen(true)} />
      <Suspense fallback={<PageSkeleton />}>
        <TabNavProvider
          goToTab={goToTab}
          openMenu={() => setDrawerOpen(true)}
          tabs={visibleTabs.map((t) => ({
            key: t.key,
            label: t.label,
            icon: t.icon,
            badge: badgeFor(t.key),
          }))}
          activeIndex={safeIndex}
        >
          {active?.element ?? null}
        </TabNavProvider>

        <MobileTabBar
          items={primaryTabs.map((t) => ({
            key: t.key,
            label: t.label,
            icon: t.icon,
            badge: badgeFor(t.key),
          }))}
          activeKey={primaryTabs.some((t) => t.key === active?.key) ? active.key : false}
          onSelect={goToTab}
        />
      </Suspense>

      <NavigationDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        items={visibleTabs.map((t) => ({
          key: t.key,
          label: t.label,
          icon: t.icon,
          group: t.group,
          badge: badgeFor(t.key),
        }))}
        activeKey={active?.key}
        onSelect={goToTab}
      />
    </AppContainer>
  );
};

const App: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number): void => {
    setCurrentTab(newValue);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <PermissionsProvider>
        <NasabahProvider>
        <PinGateProvider>
          <Router>
            <Routes>
          {/* Public Routes */}
          <Route 
            path="/" 
            element={
              <Suspense fallback={<PageSkeleton />}>
                <LandingPage />
              </Suspense>
            } 
          />
          <Route
            path="/login"
            element={
              <PublicOnlyRoute>
                <Suspense fallback={<PageSkeleton />}>
                  <LoginPage />
                </Suspense>
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicOnlyRoute>
                <Suspense fallback={<PageSkeleton />}>
                  <RegisterPage />
                </Suspense>
              </PublicOnlyRoute>
            }
          />
          <Route 
            path="/auth/callback" 
            element={
              <Suspense fallback={<PageSkeleton />}>
                <AuthCallbackPage />
              </Suspense>
            } 
          />
          <Route
            path="/select-branch"
            element={
              <Suspense fallback={<PageSkeleton />}>
                <BranchSelectionPage />
              </Suspense>
            }
          />
          <Route
            path="/daftar-bank-sampah"
            element={
              <Suspense fallback={<PageSkeleton />}>
                <BankSampahRegisterPage />
              </Suspense>
            }
          />
          <Route
            path="/aktivasi"
            element={
              <Suspense fallback={<PageSkeleton />}>
                <AktivasiPage />
              </Suspense>
            }
          />
          
          {/* Protected Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <DashboardWrapper 
                  currentTab={currentTab}
                  onTabChange={handleTabChange}
                />
              </ProtectedRoute>
            } 
          />
          
          <Route
            path="/price-check"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageSkeleton />}>
                  <NasabahWastePriceCheckPage />
                </Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/transfer"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageSkeleton />}>
                  <TransferPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Pengelola Routes */}
          <Route
            path="/pengelola/nasabah"
            element={
              <RoleProtectedRoute allow={TENANT_OPERATIONAL_ROLES}>
                <Suspense fallback={<PageSkeleton />}>
                  <NasabahPage />
                </Suspense>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/pengelola/aktivasi"
            element={
              <RoleProtectedRoute allow={TENANT_OPERATIONAL_ROLES}>
                <Suspense fallback={<PageSkeleton />}>
                  <AktivasiNasabahPage />
                </Suspense>
              </RoleProtectedRoute>
            }
          />
          <Route 
            path="/pengelola/inventori" 
            element={
              <RoleProtectedRoute allow={TENANT_OPERATIONAL_ROLES}>
                <Suspense fallback={<PageSkeleton />}>
                  <InventoryPage />
                </Suspense>
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="/pengelola/statistik" 
            element={
              <RoleProtectedRoute allow={TENANT_OPERATIONAL_ROLES}>
                <Suspense fallback={<PageSkeleton />}>
                  <StatistikPage />
                </Suspense>
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="/pengelola/pengguna" 
            element={
              <RoleProtectedRoute allow={TENANT_ADMIN_ROLES}>
                <Suspense fallback={<PageSkeleton />}>
                  <ManajemenPenggunaPage />
                </Suspense>
              </RoleProtectedRoute>
            } 
          />
          <Route
            path="/pengelola/harga"
            element={
              <RoleProtectedRoute allow={TENANT_OPERATIONAL_ROLES}>
                <Suspense fallback={<PageSkeleton />}>
                  <PengaturanHargaPage />
                </Suspense>
              </RoleProtectedRoute>
            }
          />
          <Route 
            path="/pengelola/pembayaran" 
            element={
              <RoleProtectedRoute allow={TENANT_OPERATIONAL_ROLES}>
                <Suspense fallback={<PageSkeleton />}>
                  <RiwayatPembayaranPage />
                </Suspense>
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="/pengelola/notifikasi" 
            element={
              <RoleProtectedRoute allow={TENANT_OPERATIONAL_ROLES}>
                <Suspense fallback={<PageSkeleton />}>
                  <NotifikasiPage />
                </Suspense>
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="/pengelola/backup" 
            element={
              <RoleProtectedRoute allow={TENANT_ADMIN_ROLES}>
                <Suspense fallback={<PageSkeleton />}>
                  <BackupExportPage />
                </Suspense>
              </RoleProtectedRoute>
            } 
          />
          
          {/* Logout route — memanggil authService.logout lalu redirect ke landing */}
          <Route path="/logout" element={<LogoutRedirect />} />

          {/* Redirect any unknown routes to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </PinGateProvider>
        </NasabahProvider>
        </PermissionsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;